import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Send } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { ConexionAPI, TIPO_META } from "../../lib/conexion";
import UbicacionSelector from "../../components/UbicacionSelector";

const TIPO_POR_ROL = {
  cliente: "solicitud",
  contratista: "oferta",
  ferreteria: "producto",
};

export default function ConexionPublicar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const rol = user?.role;
  const [catalogos, setCatalogos] = useState(null);
  const [tipo, setTipo] = useState(TIPO_POR_ROL[rol] || "solicitud");
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [categoria, setCategoria] = useState("");
  const [precio, setPrecio] = useState("");
  const [ubic, setUbic] = useState({
    region: user?.ubicacion?.region || "",
    provincia: user?.ubicacion?.provincia || "",
    distrito: user?.ubicacion?.distrito || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { ConexionAPI.catalogos().then(setCatalogos); }, []);

  const requiereRol = !rol || !["cliente", "contratista", "ferreteria"].includes(rol);

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (requiereRol) {
      setError("Antes de publicar, actualiza tu rol desde 'Mi perfil'.");
      return;
    }
    if (!titulo || !descripcion || !categoria || !ubic.region || !ubic.provincia || !ubic.distrito) {
      setError("Completa todos los campos obligatorios (incluye Región, Provincia y Distrito).");
      return;
    }
    setSaving(true);
    try {
      await ConexionAPI.crearPublicacion({
        titulo, descripcion, tipo, categoria,
        ubicacion: ubic,
        precio: precio ? Number(precio) : null,
        imagenes: [],
      });
      navigate("/conexion/mis-publicaciones");
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo crear la publicación.");
    } finally {
      setSaving(false);
    }
  }

  const meta = TIPO_META[tipo];

  return (
    <div className="max-w-3xl mx-auto" data-testid="conexion-publicar">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-4">
        <ArrowLeft size={16} /> Volver
      </button>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6 md:p-8">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: meta.color }}>
          Nueva publicación
        </p>
        <h2 className="mt-1 text-2xl font-extrabold text-slate-900 tracking-tight">
          Publica en ConstruLink Red
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Llega a clientes, maestros y ferreterías de todo el Perú.
        </p>

        {requiereRol && (
          <div className="mt-4 bg-amber-50 border border-amber-200 text-amber-800 text-sm px-3 py-2 rounded-lg">
            No tienes un rol de Conexión (cliente / contratista / ferretería). Ve a <b>Mi perfil</b> y asigna uno para publicar.
          </div>
        )}

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="conexion-publicar-form">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Tipo</label>
            <div className="mt-1.5 grid grid-cols-3 gap-2">
              {["solicitud", "oferta", "producto"].map((t) => {
                const active = tipo === t;
                const c = TIPO_META[t];
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTipo(t)}
                    data-testid={`conexion-publicar-tipo-${t}`}
                    className={[
                      "px-3 py-2.5 rounded-xl text-sm font-semibold border transition-all",
                      active ? "text-white border-transparent shadow-sm" : "text-slate-600 bg-white border-slate-200 hover:bg-slate-50",
                    ].join(" ")}
                    style={active ? { backgroundColor: c.color } : {}}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Título *</label>
            <input
              required maxLength={140}
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              data-testid="conexion-publicar-titulo"
              placeholder="Ej. Construcción de casa 120 m² en Trujillo"
              className="mt-1.5 w-full h-12 px-4 rounded-xl border border-slate-200 bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Descripción *</label>
            <textarea
              required minLength={5} maxLength={2000} rows={4}
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              data-testid="conexion-publicar-descripcion"
              placeholder="Describe el trabajo, producto o servicio con detalle…"
              className="mt-1.5 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none resize-none"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Categoría *</label>
              <select
                required value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
                data-testid="conexion-publicar-categoria"
                className="mt-1.5 w-full h-12 px-3 rounded-xl border border-slate-200 bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none"
              >
                <option value="">Selecciona una categoría</option>
                {catalogos?.categorias.map((c) => (
                  <option key={c.id} value={c.nombre}>{c.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Precio (S/) — opcional</label>
              <input
                type="number" min="0" step="0.01"
                value={precio}
                onChange={(e) => setPrecio(e.target.value)}
                data-testid="conexion-publicar-precio"
                placeholder="Ej. 24.50"
                className="mt-1.5 w-full h-12 px-4 rounded-xl border border-slate-200 bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <UbicacionSelector
              value={ubic}
              onChange={setUbic}
              required
              testIdPrefix="conexion-publicar"
              showTotals
            />
          </div>

          {error && (
            <p className="text-sm text-brand-red bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="conexion-publicar-error">
              {error}
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="submit" disabled={saving}
              data-testid="conexion-publicar-submit"
              className="inline-flex items-center gap-2 bg-brand-red hover:bg-brand-red-hover disabled:opacity-60 text-white font-semibold px-6 py-2.5 rounded-xl transition-colors shadow-sm"
            >
              {saving ? "Publicando…" : (<><Send size={16} /> Publicar</>)}
            </button>
            <button
              type="button" onClick={() => navigate("/conexion")}
              className="inline-flex items-center gap-2 border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold px-5 py-2.5 rounded-xl transition-colors"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
